import {IMidiDeviceStateChange, MidiEventTypes} from "./MidiEventTypes";
import {Subject, Subscription} from "rxjs";
import {logger} from "../../log/logger";
import * as Sentry from "@sentry/react";
import {NormalizedMidiInput} from "../../../models/NormalizedMidiInput";

const midiEventSubject = new Subject<MidiEventTypes>();

export function subscribeToMidiEvents(callback: (message: MidiEventTypes) => void, debugName?: string) {
  /** DEBUG: authentication lost when playing lesson */
  const numberOfMidiObserversBeforeSubscribe= midiEventSubject.observers.length;
  const subscriptionStartTime = Date.now();
  const subscriptionIdentifier = `${debugName ?? `UNKNOWN`}-${subscriptionStartTime}`;

  // Actual subscription happens here
  const subscription = midiEventSubject.subscribe((message: MidiEventTypes) => {
    try {
      callback(message);
    } catch(e) {
      logger.midiEventBus.error({message: `error encountered in observer of NotesEventTypes `, error: e as Error});
      Sentry.captureException(e);
    }
  });

  logger.midiEventBus.debug({
    message: `subscribe: ${subscriptionIdentifier}`,
    data: [
      { key: "subscription", value: subscriptionStartTime},
      { key: "midiObserversBeforeStart", value: numberOfMidiObserversBeforeSubscribe},
    ]
  });

  const unsubscribeFunction = getUnsubscribeToMidiEventsFunction(subscription, subscriptionIdentifier, subscriptionStartTime, numberOfMidiObserversBeforeSubscribe);
  return unsubscribeFunction;
}

function getUnsubscribeToMidiEventsFunction(subscription: Subscription, subscriptionIdentifier: string, subscriptionStartTime: number, numberOfMidiObserversBeforeSubscribe: number) {
  return () => {
    // Unsubscribe happens here
    subscription.unsubscribe();

    const subscriptionEndTime = Date.now();
    const subscriptionActiveTime = (subscriptionEndTime - subscriptionStartTime);
    const numberOfMidiObserversAfterUnsubscribe = midiEventSubject.observers.length;

    // build data for logging
    const midiUnsubscribeData = {
      time: {
        start: subscriptionStartTime,
        end: subscriptionEndTime,
        active: subscriptionActiveTime,
      },
      observers: {
        before: numberOfMidiObserversBeforeSubscribe,
        after: numberOfMidiObserversAfterUnsubscribe,
      },
    };

    // log midi unsubscribe
    logger.midiEventBus.debug({
      message: `unsubscribe: ${subscriptionIdentifier}`,
      data: [
        { key: "midiUnsubscribeData", value: midiUnsubscribeData},
      ]
    });
  };
}

function emitMidiEventMessage(message: MidiEventTypes){
  midiEventSubject.next(message);
}

export function midiNotePlayed(number: number, velocity: number, source: string, channel: number, receivedTime: number, deviceId: string, normalizedMidiInput: NormalizedMidiInput, deviceName?: string){
  emitMidiEventMessage({type:"midiNotePlayed", number, velocity, source, channel, receivedTime, deviceId, normalizedMidiInput, deviceName });
}

export function midiNoteStopped(number: number, source: string, channel: number, receivedTime: number, deviceId: string, normalizedMidiInput: NormalizedMidiInput, deviceName?: string){
  emitMidiEventMessage({type: "midiNoteStopped", number, source, channel, receivedTime, deviceId, normalizedMidiInput, deviceName,});
}

export function onMidiFootPedal_pressed(number: number, source: string, channel: number, receivedTime: number, deviceId: string, deviceName?: string){
  emitMidiEventMessage({type: "midiFootPedalPressed", number, source, channel, receivedTime, deviceId, deviceName,});
}

export function onMidiFootPedal_released(number: number, source: string, channel: number, receivedTime: number, deviceId: string, deviceName?: string){
  emitMidiEventMessage({type: "midiFootPedalReleased", number, source, channel, receivedTime, deviceId, deviceName,});
}

export function onMidiDeviceStateChange(port: NormalizedMidiInput | undefined) {
  emitMidiEventMessage({type: "midiDeviceStateChange", port});
}

export function midiUIControlNote(numbers: number[]){
  emitMidiEventMessage({type: "midiUIControlNote", numbers});
}
